/**
 * Central registry of TanStack Query keys. Hierarchical, so related queries can
 * be invalidated together, e.g. `invalidateQueries({ queryKey: queryKeys.boards.all })`.
 */
export const queryKeys = {
  system: {
    readiness: ['system', 'readiness'] as const,
  },
  viewer: ['viewer'] as const,
  workspaces: {
    all: ['workspaces'] as const,
    detail: (workspaceId: string) => ['workspaces', workspaceId] as const,
  },
  boards: {
    all: ['boards'] as const,
    list: (workspaceId: string) => ['boards', 'list', workspaceId] as const,
    detail: (boardId: string) => ['boards', 'detail', boardId] as const,
    /** Prefix of all search/filter results of a board. */
    searchAll: (boardId: string) => ['boards', 'search', boardId] as const,
    search: (boardId: string, filter: unknown) => ['boards', 'search', boardId, filter] as const,
    /** Prefix of all Timeline results of a board. */
    timelineAll: (boardId: string) => ['boards', 'timeline', boardId] as const,
    timeline: (boardId: string, includeCompleted: boolean, first: string, last: string) =>
      ['boards', 'timeline', boardId, { includeCompleted, first, last }] as const,
    archive: (boardId: string) => ['boards', 'archive', boardId] as const,
    trash: (boardId: string) => ['boards', 'trash', boardId] as const,
    /** Boards deleted from a workspace (workspace Trash). */
    deleted: (workspaceId: string) => ['boards', 'deleted', workspaceId] as const,
  },
  cards: {
    all: ['cards'] as const,
    detail: (cardId: string) => ['cards', cardId] as const,
    attachments: (cardId: string) => ['cards', cardId, 'attachments'] as const,
    recurrence: (cardId: string) => ['cards', cardId, 'recurrence'] as const,
    recurrenceExceptions: (cardId: string) =>
      ['cards', cardId, 'recurrence', 'occurrences'] as const,
    history: (cardId: string) => ['cards', cardId, 'history'] as const,
    versions: (cardId: string) => ['cards', cardId, 'versions'] as const,
    version: (cardId: string, versionId: string) =>
      ['cards', cardId, 'versions', versionId] as const,
  },
  /** Calendar range queries (board and workspace scope); invalidated by any card change. */
  calendar: {
    all: ['calendar'] as const,
  },
  /** The personal planner (`/me/tasks*`); the calendar of it lives under `calendar` with scope `me`. */
  myTasks: {
    all: ['my-tasks'] as const,
    summary: ['my-tasks', 'summary'] as const,
    /** Prefix of every list query. */
    listAll: ['my-tasks', 'list'] as const,
    list: (filter: unknown) => ['my-tasks', 'list', filter] as const,
  },
  /** Per-user quick-create destination of a workspace. */
  quickCreate: (workspaceId: string) => ['workspaces', workspaceId, 'quick-create'] as const,
  /** The signed-in user's Mysliarium feed (personal notes). */
  notes: {
    all: ['notes'] as const,
    feed: ['notes', 'feed'] as const,
  },
  activity: {
    board: (boardId: string) => ['boards', 'activity', boardId] as const,
  },
  /** Universal databases (independent of boards and tasks). */
  /** Color palettes + favorites of the viewer (COLOR-PALETTES-UX1). */
  palettes: {
    library: ['palettes', 'library'] as const,
  },
  /** Feedback Center (FEEDBACK-PORTAL1): private suggestions and their conversations. */
  feedback: {
    all: ['feedback'] as const,
    lists: ['feedback', 'list'] as const,
    list: (filter: unknown) => ['feedback', 'list', filter] as const,
    detail: (suggestionId: string) => ['feedback', 'detail', suggestionId] as const,
    moderators: ['feedback', 'moderators'] as const,
  },
  /** Sections that organize a workspace's boards and databases (UD-STRUCT). */
  sections: {
    list: (workspaceId: string) => ['sections', 'list', workspaceId] as const,
  },
  databases: {
    all: ['databases'] as const,
    /** Every reachable database of the viewer (home catalog). */
    mine: ['databases', 'mine'] as const,
    list: (workspaceId: string) => ['databases', 'list', workspaceId] as const,
    detail: (databaseId: string) => ['databases', 'detail', databaseId] as const,
    tables: (databaseId: string) => ['databases', 'tables', databaseId] as const,
    table: (tableId: string) => ['databases', 'table', tableId] as const,
    /** Prefix of every record page query of a table. */
    recordsAll: (tableId: string) => ['databases', 'records', tableId] as const,
    records: (tableId: string, query: unknown) => ['databases', 'records', tableId, query] as const,
    access: (databaseId: string) => ['databases', 'access', databaseId] as const,
    views: (tableId: string) => ['databases', 'views', tableId] as const,
    /** The database's History (an infinite query per filter set) and one record's. */
    history: (databaseId: string, filters: unknown) =>
      ['databases', 'history', databaseId, filters] as const,
    recordHistory: (recordId: string) => ['databases', 'record-history', recordId] as const,
  },
  /** Collaboration: everything about who may do what (workspaces, boards, invitations). */
  collab: {
    myBoards: ['collab', 'my-boards'] as const,
    myBoardsFiltered: (filter: unknown) => ['collab', 'my-boards', filter] as const,
    members: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'members'] as const,
    roles: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'roles'] as const,
    hierarchy: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'hierarchy'] as const,
    matrix: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'matrix'] as const,
    teams: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'teams'] as const,
    boardLabels: (workspaceId: string) =>
      ['collab', 'workspace', workspaceId, 'board-labels'] as const,
    rules: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'rules'] as const,
    invitations: (scope: 'workspace' | 'board' | 'database', id: string) =>
      ['collab', scope, id, 'invitations'] as const,
    links: (scope: 'workspace' | 'board' | 'database', id: string) =>
      ['collab', scope, id, 'links'] as const,
    requests: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'requests'] as const,
    boardRequests: (boardId: string) => ['collab', 'board', boardId, 'requests'] as const,
    audit: (workspaceId: string) => ['collab', 'workspace', workspaceId, 'audit'] as const,
    participants: (boardId: string) => ['collab', 'board', boardId, 'participants'] as const,
    access: (boardId: string) => ['collab', 'board', boardId, 'access'] as const,
    roleTemplates: (boardId: string) => ['collab', 'board', boardId, 'role-templates'] as const,
    boardLabelIds: (boardId: string) => ['collab', 'board', boardId, 'labels'] as const,
    myInvitations: ['collab', 'me', 'invitations'] as const,
    myRequests: ['collab', 'me', 'requests'] as const,
    notifications: ['collab', 'notifications'] as const,
    unread: ['collab', 'notifications', 'unread'] as const,
    comments: (cardId: string) => ['cards', cardId, 'comments'] as const,
    mentionable: (cardId: string) => ['cards', cardId, 'mentionable'] as const,
    watchers: (cardId: string) => ['cards', cardId, 'watchers'] as const,
    transferImpact: (boardId: string, destination: string) =>
      ['collab', 'board', boardId, 'transfer-impact', destination] as const,
    ownership: (boardId: string) => ['collab', 'board', boardId, 'ownership'] as const,
    notificationPolicy: (workspaceId: string) =>
      ['collab', 'workspace', workspaceId, 'notification-policy'] as const,
    notificationPreferences: (workspaceId: string) =>
      ['collab', 'workspace', workspaceId, 'notification-preferences'] as const,
    /** Notification inbox (unread only, the bell) and the full history page. */
    inbox: ['collab', 'notifications', 'inbox'] as const,
    history: (filter: string) => ['collab', 'notifications', 'history', filter] as const,
  },
};
